import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "../Food/RecipeList.css";
import "./BabyTracker.css";
import {
  defaultBabyEventForm,
  deleteBabyEvent,
  eventCategoryLabels,
  eventCategoryPluralLabels,
  eventTypeOptionsForCategory,
  bottleMilkTypes,
  bottleMilkTypeForEvent,
  calculateMinutesBetween,
  defaultBottleMilkType,
  feedingSides,
  feedingUnitForType,
  formatDurationFromMinutes,
  formatElapsedSeconds,
  formatFeedingAmount,
  fetchBabyEvents,
  getNowLocalDateTime,
  medicineTypes,
  normalizeFeedingType,
  saveBabyEvent,
  saveBabyEventNotes,
  sleepEventDisplay,
} from "./BabyTrackerShared";

const categoryIntro = {
  diaper: "Track wet, dirty, mixed, and dry diaper checks.",
  feeding: "Track breast feeding, bottle feeds, amounts, and notes.",
  pumping: "Track pumping sessions, timing, and ounces collected.",
  medicine: "Track medicine doses and daily reminders.",
  sleep: "Review sleep starts, wake ups, and SNOO-imported sleep sessions.",
};

const defaultTypeForCategory = (category) => eventTypeOptionsForCategory(category)[0];

const snooSessionIdFromNotes = (notes = "") => {
  const match = notes.match(/^SNOO inferred sleep session (.+)$/);
  return match ? match[1] : "";
};

const sleepSortTime = (entry) => new Date(entry?.occurredAtInput || 0).getTime();

const BabyTrackerCategoryPage = ({ category }) => {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(defaultBabyEventForm(category, defaultTypeForCategory(category)));
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerPaused, setTimerPaused] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const notesSaveTimer = useRef(null);
  const timerStartedAtMs = useRef(null);
  const timerAccumulatedSeconds = useRef(0);
  const timerDurationOverride = useRef(false);
  const isFeeding = category === "feeding";
  const isPumping = category === "pumping";
  const isMedicine = category === "medicine";
  const isBreastFeeding = isFeeding && form.eventType === "Breast";
  const isBottleFeeding = isFeeding && form.eventType === "Bottle";
  const title = eventCategoryPluralLabels[category];

  const groupedSleepEvents = useMemo(() => {
    if (category !== "sleep") return [];

    const snooSessions = new Map();
    const manualEvents = [];
    events.forEach((entry) => {
      const sessionId = snooSessionIdFromNotes(entry.notes);
      if (!sessionId) {
        manualEvents.push({ kind: "manual", sortTime: sleepSortTime(entry), entry });
        return;
      }

      const session = snooSessions.get(sessionId) || {
        kind: "snoo",
        sessionId,
        sleep: null,
        wake: null,
      };
      if (entry.eventType === "Wake Up") {
        session.wake = entry;
      } else {
        session.sleep = entry;
      }
      snooSessions.set(sessionId, session);
    });

    const sessionEvents = Array.from(snooSessions.values()).map((session) => ({
      ...session,
      sortTime: Math.max(sleepSortTime(session.sleep), sleepSortTime(session.wake)),
    }));

    return [...sessionEvents, ...manualEvents].sort((left, right) => right.sortTime - left.sortTime);
  }, [category, events]);

  const refreshEvents = useCallback(() => {
    fetchBabyEvents(category).then((data) => setEvents(data || []));
  }, [category]);

  useEffect(() => {
    setForm(defaultBabyEventForm(category, defaultTypeForCategory(category)));
    setEditingId(null);
    setShowForm(false);
    setStatusMessage("");
    setTimerRunning(false);
    setTimerPaused(false);
    setElapsedSeconds(0);
    timerStartedAtMs.current = null;
    timerAccumulatedSeconds.current = 0;
    timerDurationOverride.current = false;
  }, [category]);

  useEffect(() => {
    refreshEvents();
  }, [refreshEvents]);

  useEffect(() => {
    if (!timerRunning || !timerStartedAtMs.current) return undefined;
    const intervalId = setInterval(() => {
      const currentRunSeconds = Math.max(0, Math.floor((Date.now() - timerStartedAtMs.current) / 1000));
      setElapsedSeconds(timerAccumulatedSeconds.current + currentRunSeconds);
    }, 1000);
    return () => clearInterval(intervalId);
  }, [timerRunning]);

  useEffect(() => {
    if (!editingId || !showForm) return undefined;
    if (notesSaveTimer.current) {
      clearTimeout(notesSaveTimer.current);
    }
    notesSaveTimer.current = setTimeout(() => {
      saveBabyEventNotes(editingId, form.notes || "").catch(() => {
        setStatusMessage("Notes could not autosave.");
      });
    }, 900);
    return () => {
      if (notesSaveTimer.current) {
        clearTimeout(notesSaveTimer.current);
      }
    };
  }, [editingId, form.notes, showForm]);

  useEffect(() => {
    if (!isBreastFeeding || !form.occurredAt || !form.endedAt || timerDurationOverride.current) return;
    const minutes = calculateMinutesBetween(form.occurredAt, form.endedAt);
    if (minutes !== "" && minutes !== String(form.feedingAmount)) {
      setForm((currentValue) => ({ ...currentValue, feedingAmount: minutes }));
    }
  }, [form.occurredAt, form.endedAt, form.feedingAmount, isBreastFeeding]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((currentValue) => {
      if (name === "eventType" && category === "feeding") {
        timerDurationOverride.current = false;
        return {
          ...currentValue,
          eventType: value,
          feedingUnit: feedingUnitForType(value),
          feedingMilkType: value === "Bottle" ? currentValue.feedingMilkType || defaultBottleMilkType : "",
          feedingSide: value === "Breast" ? currentValue.feedingSide : "",
          endedAt: value === "Breast" ? currentValue.endedAt : "",
        };
      }
      if (category === "pumping" && name === "eventType") {
        return {
          ...currentValue,
          eventType: value,
          feedingUnit: "oz",
          feedingMilkType: "",
          feedingSide: "",
        };
      }
      if (category === "medicine" && name === "eventType") {
        return {
          ...currentValue,
          eventType: value,
          medicineOtherName: value === "Other" ? currentValue.medicineOtherName : "",
          endedAt: "",
          feedingAmount: "",
          feedingUnit: "",
          feedingMilkType: "",
          feedingSide: "",
        };
      }
      if (name === "occurredAt" || name === "endedAt") {
        timerDurationOverride.current = false;
      }
      return { ...currentValue, [name]: value };
    });
  };

  const resetBreastFeedingTimer = () => {
    setTimerRunning(false);
    setTimerPaused(false);
    setElapsedSeconds(0);
    timerStartedAtMs.current = null;
    timerAccumulatedSeconds.current = 0;
    timerDurationOverride.current = false;
  };

  const getCurrentTimerSeconds = () => {
    if (!timerRunning || !timerStartedAtMs.current) return elapsedSeconds;
    const currentRunSeconds = Math.max(0, Math.floor((Date.now() - timerStartedAtMs.current) / 1000));
    return timerAccumulatedSeconds.current + currentRunSeconds;
  };

  const formatTimerMinutes = (seconds) => String(Math.max(0, Math.round(seconds)) / 60);

  const startBreastFeedingTimer = () => {
    if (timerPaused) {
      timerStartedAtMs.current = Date.now();
      timerAccumulatedSeconds.current = elapsedSeconds;
      setTimerPaused(false);
      setTimerRunning(true);
      setStatusMessage("Breast feeding timer resumed.");
      return;
    }

    const startTime = getNowLocalDateTime();
    const timerForm = {
      ...form,
      eventCategory: category,
      eventType: "Breast",
      occurredAt: startTime,
      endedAt: "",
      feedingAmount: "",
      feedingUnit: "min",
      feedingMilkType: "",
      feedingSide: form.feedingSide || "",
    };
    timerDurationOverride.current = false;
    timerStartedAtMs.current = Date.now();
    timerAccumulatedSeconds.current = 0;
    setTimerRunning(true);
    setTimerPaused(false);
    setElapsedSeconds(0);
    setForm(timerForm);
    if (!editingId) {
      saveBabyEvent(timerForm)
        .then((data) => {
          const savedId = data?.event?.id || data?.id;
          if (savedId) {
            setEditingId(savedId);
            setStatusMessage("Breast feeding timer started. Notes will autosave.");
            refreshEvents();
          }
        })
        .catch(() => {
          setTimerRunning(false);
          setStatusMessage("Unable to start timer.");
        });
    }
  };

  const pauseBreastFeedingTimer = () => {
    const currentElapsedSeconds = getCurrentTimerSeconds();
    timerAccumulatedSeconds.current = currentElapsedSeconds;
    timerStartedAtMs.current = null;
    setElapsedSeconds(currentElapsedSeconds);
    setTimerRunning(false);
    setTimerPaused(true);
    setStatusMessage("Breast feeding timer paused.");
  };

  const stopBreastFeedingTimer = () => {
    const endTime = getNowLocalDateTime();
    const finalElapsedSeconds = getCurrentTimerSeconds();
    timerDurationOverride.current = true;
    timerAccumulatedSeconds.current = finalElapsedSeconds;
    timerStartedAtMs.current = null;
    const stoppedForm = {
      ...form,
      endedAt: endTime,
      feedingAmount: formatTimerMinutes(finalElapsedSeconds),
    };
    setTimerRunning(false);
    setTimerPaused(false);
    setElapsedSeconds(finalElapsedSeconds);
    setForm(stoppedForm);
    if (editingId) {
      saveBabyEvent(stoppedForm, editingId)
        .then(() => {
          setStatusMessage("Breast feeding timer stopped.");
          refreshEvents();
        })
        .catch(() => {
          setStatusMessage("Unable to save stopped timer.");
        });
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setStatusMessage("");
    const submittedEndTime = isBreastFeeding && (timerRunning || timerPaused) ? getNowLocalDateTime() : form.endedAt;
    const submittedTimerSeconds = getCurrentTimerSeconds();
    const medicineName = form.eventType === "Other" ? (form.medicineOtherName || "").trim() : form.eventType;
    const eventForm = {
      ...form,
      eventType: category === "medicine" ? medicineName || "Other" : form.eventType,
      endedAt: submittedEndTime,
      feedingUnit: category === "feeding" ? feedingUnitForType(form.eventType) : category === "pumping" ? "oz" : "",
      feedingAmount: isBreastFeeding && (timerRunning || timerPaused) ? formatTimerMinutes(submittedTimerSeconds) : form.feedingAmount,
      feedingMilkType: category === "feeding" && form.eventType === "Bottle" ? form.feedingMilkType || defaultBottleMilkType : "",
      feedingSide: category === "feeding" && form.eventType === "Breast" ? form.feedingSide : "",
    };
    saveBabyEvent(eventForm, editingId)
      .then(() => {
        setStatusMessage(editingId ? `${eventCategoryLabels[category]} event updated.` : `${eventCategoryLabels[category]} event logged.`);
        setEditingId(null);
        setShowForm(false);
        resetBreastFeedingTimer();
        setForm(defaultBabyEventForm(category, defaultTypeForCategory(category)));
        refreshEvents();
      })
      .catch(() => {
        setStatusMessage("Unable to save event.");
      });
  };

  const beginEdit = (entry) => {
    setEditingId(entry.id);
    setShowForm(true);
    setStatusMessage("");
    const normalizedEventType = normalizeFeedingType(entry.eventType);
    const medicineIsKnown = medicineTypes.includes(entry.eventType);
    setForm({
      eventCategory: category,
      eventType: category === "medicine" && !medicineIsKnown ? "Other" : normalizedEventType,
      occurredAt: entry.occurredAtInput,
      endedAt: entry.endedAtInput || "",
      feedingAmount: entry.feedingAmount ?? "",
      feedingUnit: entry.feedingUnit || feedingUnitForType(normalizedEventType),
      feedingMilkType: normalizedEventType === "Bottle" ? bottleMilkTypeForEvent(entry) : "",
      feedingSide: normalizedEventType === "Breast" ? entry.feedingSide || "" : "",
      medicineOtherName: category === "medicine" && !medicineIsKnown ? entry.eventType : "",
      notes: entry.notes || "",
    });
    setTimerRunning(false);
    setTimerPaused(false);
    setElapsedSeconds(0);
    timerStartedAtMs.current = null;
    timerAccumulatedSeconds.current = 0;
    timerDurationOverride.current = false;
  };

  const removeEvent = (entry) => {
    if (!window.confirm(`Delete this ${eventCategoryLabels[category].toLowerCase()} event?`)) {
      return;
    }
    deleteBabyEvent(entry.id).then(() => {
      setStatusMessage(`${eventCategoryLabels[category]} event deleted.`);
      setEditingId(null);
      setShowForm(false);
      resetBreastFeedingTimer();
      setForm(defaultBabyEventForm(category, defaultTypeForCategory(category)));
      refreshEvents();
    });
  };

  return (
    <div className="BabyTrackerPage">
      <div className="searchBarDiv formViewWrapper BabyTrackerIntro">
        <div>
          <h2 className="BabyTrackerTitle">{title}</h2>
          <p className="BabyTrackerSubtitle">{categoryIntro[category]}</p>
        </div>
        <Link className="summaryButton BabyTrackerLinkButton" to="/baby-tracker">
          Home
        </Link>
      </div>

      <div className="BabyTrackerGrid">
        <div className="searchBarDiv formViewWrapper BabyTrackerPanel BabyTrackerSinglePanel">
          {!showForm ? (
            <button
              className="summaryButton BabyTrackerAddToggleButton"
              onClick={() => {
                setEditingId(null);
                resetBreastFeedingTimer();
                setForm(defaultBabyEventForm(category, defaultTypeForCategory(category)));
                setShowForm(true);
              }}
            >
              Add {eventCategoryLabels[category]}
            </button>
          ) : (
            <div className="BabyTrackerFormBlock">
              <h2 className="BabyTrackerTitle">{editingId ? `Edit ${eventCategoryLabels[category].toLowerCase()} event` : `Add ${eventCategoryLabels[category].toLowerCase()} event`}</h2>
              <form onSubmit={handleSubmit}>
                <div className="BabyTrackerTwoColumn">
                  <div className="inputDiv">
                    <label>Type</label>
                    <select
                      className="inputBox addInput"
                      name="eventType"
                      value={form.eventType}
                      onChange={handleChange}
                    >
                      {eventTypeOptionsForCategory(category).map((typeValue) => (
                        <option key={typeValue} value={typeValue}>
                          {typeValue}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="inputDiv">
                    <label>Occurred</label>
                    <input
                      type="datetime-local"
                      step="1"
                      className="inputBox addInput"
                      name="occurredAt"
                      value={form.occurredAt}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {isFeeding ? (
                  <>
                    {isBreastFeeding ? (
                      <div className="BabyTrackerTimerPanel">
                        <div>
                          <span>Elapsed</span>
                          <strong>{formatElapsedSeconds(timerRunning || timerPaused ? elapsedSeconds : Number(form.feedingAmount || 0) * 60)}</strong>
                        </div>
                        <div className="BabyTrackerActionRow">
                          <button
                            className="summaryButton"
                            disabled={timerRunning}
                            onClick={startBreastFeedingTimer}
                            type="button"
                          >
                            {timerPaused ? "Resume" : "Start"}
                          </button>
                          <button
                            className="summaryButton"
                            disabled={!timerRunning}
                            onClick={pauseBreastFeedingTimer}
                            type="button"
                          >
                            Pause
                          </button>
                          <button
                            className="summaryButton"
                            disabled={!timerRunning && !timerPaused}
                            onClick={stopBreastFeedingTimer}
                            type="button"
                          >
                            Stop
                          </button>
                        </div>
                      </div>
                    ) : null}
                    <div className="BabyTrackerTwoColumn">
                      <div className="inputDiv">
                        <label>{isBreastFeeding ? "Minutes" : "Amount (oz)"}</label>
                        <input
                          className="inputBox addInput"
                          name="feedingAmount"
                          value={isBreastFeeding ? formatDurationFromMinutes(form.feedingAmount) : form.feedingAmount}
                          onChange={handleChange}
                          inputMode="decimal"
                          readOnly={isBreastFeeding}
                        />
                      </div>
                      {isBreastFeeding ? (
                        <div className="inputDiv">
                          <label>Ended</label>
                          <input
                            type="datetime-local"
                            step="1"
                            className="inputBox addInput"
                            name="endedAt"
                            value={form.endedAt}
                            onChange={handleChange}
                          />
                        </div>
                      ) : null}
                    {isBreastFeeding ? (
                      <div className="inputDiv">
                        <label>Started side</label>
                        <select
                          className="inputBox addInput"
                          name="feedingSide"
                          value={form.feedingSide}
                          onChange={handleChange}
                        >
                          {feedingSides.map((sideValue) => (
                            <option key={sideValue || "none"} value={sideValue}>
                              {sideValue || "N/A"}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : null}
                    {isBottleFeeding ? (
                      <div className="inputDiv">
                        <label>Bottle type</label>
                        <select
                          className="inputBox addInput"
                          name="feedingMilkType"
                          value={form.feedingMilkType || defaultBottleMilkType}
                          onChange={handleChange}
                        >
                          {bottleMilkTypes.map((milkType) => (
                            <option key={milkType} value={milkType}>
                              {milkType}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : null}
                    </div>
                  </>
                ) : null}

                {isPumping ? (
                  <div className="BabyTrackerTwoColumn">
                    <div className="inputDiv">
                      <label>Ended</label>
                      <input
                        type="datetime-local"
                        step="1"
                        className="inputBox addInput"
                        name="endedAt"
                        value={form.endedAt}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="inputDiv">
                      <label>Ounces collected</label>
                      <input
                        className="inputBox addInput"
                        name="feedingAmount"
                        value={form.feedingAmount}
                        onChange={handleChange}
                        inputMode="decimal"
                      />
                    </div>
                  </div>
                ) : null}

                {isMedicine && form.eventType === "Other" ? (
                  <div className="inputDiv">
                    <label>Medicine name</label>
                    <input
                      className="inputBox addInput"
                      name="medicineOtherName"
                      value={form.medicineOtherName || ""}
                      onChange={handleChange}
                    />
                  </div>
                ) : null}

                <div className="inputDiv">
                  <label>Notes</label>
                  <textarea
                    className="inputBox textAreaAdd BabyTrackerTextarea"
                    name="notes"
                    value={form.notes}
                    onChange={handleChange}
                  />
                </div>

                <div className="BabyTrackerActionRow">
                  <button type="submit" className="summaryButton">
                    Submit
                  </button>
                  <button
                    type="button"
                    className="summaryButton"
                    onClick={() => {
                      setEditingId(null);
                      setShowForm(false);
                      resetBreastFeedingTimer();
                      setForm(defaultBabyEventForm(category, defaultTypeForCategory(category)));
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}
          {statusMessage ? <p className="BabyTrackerStatus">{statusMessage}</p> : null}
        </div>

        <div className="shoppingListDiv BabyTrackerPanel">
          <h2 className="BabyTrackerTitle">Recent {title.toLowerCase()}</h2>
          {category === "sleep" && groupedSleepEvents.length ? (
            groupedSleepEvents.map((sleepItem) => {
              if (sleepItem.kind === "snoo") {
                const durationMinutes = sleepItem.sleep && sleepItem.wake
                  ? calculateMinutesBetween(sleepItem.sleep.occurredAtInput, sleepItem.wake.occurredAtInput)
                  : "";
                return (
                  <div className="BabyTrackerHistoryCard BabyTrackerSleepCard" key={sleepItem.sessionId}>
                    <div className="BabyTrackerSleepHeader">
                      <span className="BabyTrackerSleepBadge BabyTrackerSleepBadge-sleep">
                        SNOO
                      </span>
                      <div>
                        <strong>SNOO sleep</strong>
                        <div>
                          {sleepItem.sleep ? sleepItem.sleep.occurredAt : "Started before tracked samples"}
                          {" - "}
                          {sleepItem.wake ? sleepItem.wake.occurredAt : "Still sleeping"}
                        </div>
                      </div>
                    </div>
                    {durationMinutes ? (
                      <div className="BabyTrackerHistoryMeta">
                        <span>{formatDurationFromMinutes(durationMinutes)}</span>
                      </div>
                    ) : null}
                    <p className="BabyTrackerNotes">SNOO inferred sleep session {sleepItem.sessionId}</p>
                    <div className="BabyTrackerActionRow">
                      {sleepItem.sleep ? (
                        <>
                          <button className="summaryButton" onClick={() => beginEdit(sleepItem.sleep)}>
                            Edit start
                          </button>
                          <button className="summaryButton" onClick={() => removeEvent(sleepItem.sleep)}>
                            Delete start
                          </button>
                        </>
                      ) : null}
                      {sleepItem.wake ? (
                        <>
                          <button className="summaryButton" onClick={() => beginEdit(sleepItem.wake)}>
                            Edit wake
                          </button>
                          <button className="summaryButton" onClick={() => removeEvent(sleepItem.wake)}>
                            Delete wake
                          </button>
                        </>
                      ) : null}
                    </div>
                  </div>
                );
              }

              const sleepDisplay = sleepEventDisplay(sleepItem.entry.eventType);
              return (
                <div className="BabyTrackerHistoryCard BabyTrackerSleepCard" key={sleepItem.entry.id}>
                  <div className="BabyTrackerSleepHeader">
                    <span className={`BabyTrackerSleepBadge BabyTrackerSleepBadge-${sleepDisplay.tone}`}>
                      {sleepItem.entry.eventType}
                    </span>
                    <div>
                      <strong>{sleepDisplay.label}</strong>
                      <div>{sleepItem.entry.occurredAt}</div>
                    </div>
                  </div>
                  {sleepItem.entry.notes ? <p className="BabyTrackerNotes">{sleepItem.entry.notes}</p> : null}
                  <div className="BabyTrackerActionRow">
                    <button className="summaryButton" onClick={() => beginEdit(sleepItem.entry)}>
                      Edit
                    </button>
                    <button className="summaryButton" onClick={() => removeEvent(sleepItem.entry)}>
                      Delete
                    </button>
                  </div>
                </div>
              );
            })
          ) : category === "sleep" ? (
            <p>No {title.toLowerCase()} logged yet.</p>
          ) : events.length ? (
            events.map((entry) => {
              return (
                <div className="BabyTrackerHistoryCard" key={entry.id}>
                  {category === "pumping" || (category === "feeding" && normalizeFeedingType(entry.eventType) === "Breast") ? (
                    <div className="BabyTrackerHistoryRow BabyTrackerTimedEventRow">
                      <div>
                        <strong>{category === "feeding" ? "Breast" : entry.eventType}</strong>
                        <div>Started {entry.occurredAt}</div>
                      </div>
                      {entry.endedAt ? <span>Ended {entry.endedAt}</span> : null}
                    </div>
                  ) : (
                    <div className="BabyTrackerHistoryRow">
                      <div>
                        <strong>{category === "feeding" ? normalizeFeedingType(entry.eventType) : entry.eventType}</strong>
                        <div>{entry.occurredAt}</div>
                      </div>
                      {entry.endedAt ? <span>Ended {entry.endedAt}</span> : null}
                    </div>
                  )}
                  {entry.feedingAmount ? (
                    <div className="BabyTrackerHistoryMeta">
                      <span>{formatFeedingAmount(entry)}</span>
                      {normalizeFeedingType(entry.eventType) === "Bottle" ? <span>{bottleMilkTypeForEvent(entry)}</span> : null}
                      {entry.feedingSide ? <span>{entry.feedingSide}</span> : null}
                    </div>
                  ) : null}
                  {entry.notes ? <p className="BabyTrackerNotes">{entry.notes}</p> : null}
                  <div className="BabyTrackerActionRow">
                    <button className="summaryButton" onClick={() => beginEdit(entry)}>
                      Edit
                    </button>
                    <button className="summaryButton" onClick={() => removeEvent(entry)}>
                      Delete
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <p>No {title.toLowerCase()} logged yet.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BabyTrackerCategoryPage;
